import React from 'react';
import { useProgressDisplayStore } from '../../stores/progressDisplayStore';
import { useXpReward } from '../../hooks/useXpReward';
import { useUserStats } from '../../hooks/useUserStats';
import { useCategories } from '../../hooks/useCategories';
import { useTheme, METANOIA_GRADIENT_BAR } from '../../hooks/useTheme';

const clamp = (n: number) => Math.max(0, Math.min(100, isNaN(n) ? 0 : n));

// At least 2 digits, except 0 and 100+ (matches HeaderReward.pad2).
function pad2(n: number): string {
  const v = Math.max(0, Math.round(isNaN(n) ? 0 : n));
  return v === 0 || v >= 100 ? String(v) : String(v).padStart(2, '0');
}

/**
 * Floating desktop XP-reward toast — horizontally centered at the top or
 * bottom edge (Settings → Desktop reward → Toast position), away from panel
 * content. Transient: slides + fades in on a gain with a theme-aware
 * emphasis shadow (dark halo on light theme, light glow on dark), animates
 * the user-level bar and the gaining domain's bar, then vanishes.
 * Desktop-only and gated on the 'toast' surface setting.
 */
export function DesktopXpToast() {
  const enabled = useProgressDisplayStore((s) => s.desktopSurfaces.includes('toast'));
  const position = useProgressDisplayStore((s) => s.toastPosition);
  const reward = useXpReward();
  const { stats } = useUserStats();
  const { getCategoryName } = useCategories();
  const { isMetanoia, primaryColorCSS } = useTheme();

  if (!enabled) return null;

  const barFill = isMetanoia ? METANOIA_GRADIENT_BAR : primaryColorCSS;
  const domColor = reward.domainColor || 'rgb(var(--primary))';
  const domName = reward.domainId ? getCategoryName(reward.domainId) : '';

  // slide in from the chosen edge
  const hiddenShift = position === 'top' ? 'translate(-50%, -12px)' : 'translate(-50%, 12px)';

  return (
    <div
      className={`pointer-events-none fixed left-1/2 z-50 hidden lg:block ${position === 'top' ? 'top-5' : 'bottom-5'}`}
      style={{
        opacity: reward.active ? 1 : 0,
        transform: reward.active ? 'translate(-50%, 0)' : hiddenShift,
        transition: 'opacity 400ms ease-out, transform 400ms ease-out',
      }}
      aria-hidden={!reward.active}
    >
      <div
        className={`w-64 rounded-xl border border-border bg-card/95 p-3 backdrop-blur transition-shadow duration-500 ${
          reward.active
            ? 'shadow-[0_10px_44px_-6px_rgba(0,0,0,0.45)] dark:shadow-[0_10px_44px_-6px_rgba(255,255,255,0.22)]'
            : 'shadow-none'
        }`}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">
            Level {pad2(reward.userLevel || stats.level)}
          </span>
          <span className="text-sm font-bold italic" style={{ color: domColor }}>
            +{reward.amount} XP
          </span>
        </div>

        {/* User-level bar — static gradient revealed via clip-path (cyan→purple→orange). */}
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: barFill,
              clipPath: `inset(0 ${100 - clamp(reward.userPct)}% 0 0 round 9999px)`,
            }}
          />
        </div>

        {/* Gaining domain bar */}
        {reward.domainId && (
          <div className="mt-2">
            <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
              <span className="truncate">{domName}</span>
              <span className="tabular-nums">{pad2(reward.domPct)}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full"
                style={{ width: `${clamp(reward.domPct)}%`, background: domColor }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
